/**
 * GatewayDhcp — Gateway > Configure > DHCP.
 *
 * The Meraki MX "DHCP" page for a uCentral whitebox gateway: one titled block per LAN network,
 * setting name on the left and control on the right, stacked — every downstream scope on the
 * chosen gateway in one view. It is the sibling of Gateway > VLANs and deliberately reads as one:
 * same header, same gateway picker, same locked WAN card, same propose-then-confirm dialog
 * (components/gateway/GatewayChrome). No LAN "mode" concept exists here, on purpose.
 *
 * WHAT MAKES THIS SCREEN DIFFERENT, AND HOW THE LAYOUT CARRIES IT
 *
 *   1. PROVENANCE IS ON THE PAGE, NOT IN A TOOLTIP. Every value is CONFIGURED (what the
 *      controller asked for), REPORTED (sent by the gateway in a dated state record) or
 *      UNVERIFIED (the firmware can report it, but it has never been seen on this hardware).
 *      The tags use neutral ink, never the status ramp: "configured" is not a health state.
 *
 *   2. FIXED IP ASSIGNMENTS DO NOT WORK ON TIP v5.1.0-rc2, and the screen says so beside every
 *      one. The schema accepts `ipv4.dhcp-leases[]`; two renderer defects stop dnsmasq from
 *      ever applying it. The server computes `functional` from the reported firmware (never
 *      true from a version string alone) and this page never lets an operator read an
 *      assignment as working.
 *
 *   3. THE "CURRENT LEASES" PANEL IS NEVER OMITTED. Leaving it out would let a scope's range be
 *      mistaken for the lease list. It renders one of: observed (a table, stamped with the
 *      record time) / not reported / interface down / no device state.
 *
 *   4. OFFSETS, NOT ADDRESSES. The schema stores the range start and each fixed IP as an
 *      offset into the subnet. Operators type ADDRESSES here; the server converts; and a subnet
 *      change (made on the VLANs screen) moves every assignment — the plan says so.
 *
 * NOTHING applies on first click: a save or delete opens the shared review dialog, which runs
 * POST /gateway/plan (read-only) and only then offers /gateway/apply with the plan_token.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useDevices, useGatewayDhcp, useSetGatewayDhcpLabel } from "@/lib/api/hooks";
import type {
  GatewayDhcp as GatewayDhcpData,
  GatewayDhcpReservation,
  GatewayDhcpScope,
  GatewayLeaseVisibility,
  GatewayPlan,
  GatewayReservationSupport,
} from "@/types/domain";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { relTime, fmtAbsolute } from "@/lib/format";
import { cn } from "@/lib/cn";
import {
  DeltaChip,
  GatewayPicker,
  GatewayReviewDialog,
  INPUT,
  LockedWanCard,
  NotEditableBanner,
  type PendingGatewayChange,
} from "@/components/gateway/GatewayChrome";

/** A pending change plus the controller-side label writes that must follow a SUCCESSFUL push
 *  (a label for an assignment the device refused would name something that does not exist). */
interface PendingDhcpChange extends PendingGatewayChange {
  labelFollowUp?: {
    interface: string;
    set?: { macaddr: string; label: string };
    clear?: string[];
  };
}

export default function GatewayDhcp() {
  // Gateways only, one at a time — a DHCP scope is a property of a box, not of a site.
  const { data: devices, isLoading: devicesLoading, error: devicesError } = useDevices({
    kind: "gateway",
    limit: 100,
  });
  const gateways = devices?.items ?? [];
  const [serial, setSerial] = useState<string | null>(null);
  useEffect(() => {
    const first = gateways[0];
    if (!serial && first) setSerial(first.serial);
  }, [gateways, serial]);

  const { data, isLoading, error } = useGatewayDhcp(serial ?? undefined);
  const setLabel = useSetGatewayDhcpLabel(serial ?? undefined);

  const [pending, setPending] = useState<PendingDhcpChange | null>(null);
  const [editingFixed, setEditingFixed] = useState<{
    scope: GatewayDhcpScope;
    row?: GatewayDhcpReservation;
  } | null>(null);

  // Switching gateways must not carry a half-open dialog across boxes.
  useEffect(() => {
    setPending(null);
    setEditingFixed(null);
  }, [serial]);

  const editable = !!data?.editable;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="DHCP"
          subtitle="What each LAN network hands out — address range, lease time, DNS and fixed IP assignments"
        />
        <div className="flex items-center gap-2">
          <GatewayPicker
            serial={serial}
            gateways={gateways.map((g) => ({ serial: g.serial, name: g.name }))}
            loading={devicesLoading}
            onChange={setSerial}
          />
        </div>
      </div>

      {devicesError && <ErrorBanner error={devicesError} />}
      {error && <ErrorBanner error={error} />}

      {!devicesLoading && gateways.length === 0 && (
        <EmptyState
          title="No gateways in this scope"
          hint="This screen configures a uCentral whitebox gateway. Onboard one, or switch to a site that has one."
        />
      )}

      {isLoading && serial && <div className="text-sm text-ink-muted">Loading DHCP…</div>}

      {data && (
        <div className="mt-1 flex flex-col gap-3">
          {!editable && data.reason && (
            <NotEditableBanner title="This gateway's DHCP cannot be edited yet" reason={data.reason} />
          )}

          {editable && <ProvenanceBar data={data} />}

          {data.upstream.map((u) => (
            <LockedWanCard
              key={u.location}
              name={u.name}
              summary={
                u.addressing === "dynamic"
                  ? "DHCP client"
                  : u.interface_ip
                    ? `${u.interface_ip} · ${u.subnet ?? ""}`
                    : (u.addressing ?? "—")
              }
              ports={u.ports}
              reason={u.danger_reason}
            >
              <p className="mt-2 text-sm text-ink">
                No DHCP server can run on the upstream interface. The gateway's DHCP template has
                no role check, so a scope here would hand addresses out onto the ISP-facing
                segment — the controller refuses it.
              </p>
            </LockedWanCard>
          ))}

          {data.scopes.map((scope) => (
            <ScopeBlock
              // Keyed on the configured values so a successful apply re-seeds the form from
              // what the server now holds, rather than keeping the operator's stale edits.
              key={`${scope.location}:${JSON.stringify(scope.dhcp)}`}
              scope={scope}
              support={data.reservations_support}
              editable={editable}
              onReview={setPending}
              onAddFixed={() => setEditingFixed({ scope })}
              onEditFixed={(row) => setEditingFixed({ scope, row })}
              onDeleteFixed={(row) =>
                setPending({
                  intent: "dhcp.reservation.delete",
                  spec: { interface: scope.name ?? "", macaddrs: [row.macaddr] },
                  label: "Delete fixed IP",
                  labelFollowUp: row.label
                    ? { interface: scope.name ?? "", clear: [row.macaddr] }
                    : undefined,
                })
              }
            />
          ))}

          {data.scopes.length === 0 && editable && (
            <EmptyState
              title="This gateway has no LAN networks"
              hint="DHCP is served per LAN network. Add one under Gateway → Site VLANs first."
            />
          )}

          <p className="text-[0.6875rem] text-ink-subtle">
            {data.config_uuid != null ? `Config #${data.config_uuid} · ` : ""}
            Every change is previewed before it is pushed, and a push replaces the gateway's
            whole configuration. A DHCP change is not reported back by the device, so read-back
            cannot confirm it.
          </p>
        </div>
      )}

      {editingFixed && data && (
        <FixedIpDialog
          scope={editingFixed.scope}
          row={editingFixed.row}
          support={data.reservations_support}
          onClose={() => setEditingFixed(null)}
          onLabelOnly={(label) => {
            // A label change touches no device, so it skips plan/confirm entirely.
            setLabel.mutate({
              interface: editingFixed.scope.name ?? "",
              macaddr: editingFixed.row?.macaddr ?? "",
              label,
            });
            setEditingFixed(null);
          }}
          onReview={(change) => {
            setEditingFixed(null);
            setPending(change);
          }}
        />
      )}

      {pending && serial && (
        <GatewayReviewDialog
          serial={serial}
          change={pending}
          onClose={() => setPending(null)}
          onApplied={() => setPending(null)}
          onPushed={() => {
            const f = pending.labelFollowUp;
            if (!f) return;
            f.clear?.forEach((mac) =>
              setLabel.mutate({ interface: f.interface, macaddr: mac, label: "" }),
            );
            if (f.set) setLabel.mutate({ interface: f.interface, ...f.set });
          }}
          delta={(plan) => <DhcpDelta plan={plan} />}
          outcomeText={(r) =>
            `Read-back verdict: ${r.verdict ?? "pending"}. DHCP ranges and fixed IP assignments are not reported in a uCentral state document, so no read-back can confirm this change is running — only that the gateway accepted the configuration.`
          }
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Provenance
 * ------------------------------------------------------------------ */

type Provenance = "configured" | "reported" | "unverified";

const PROVENANCE: Record<Provenance, { label: string; title: string; mark: string; chip: string }> = {
  configured: {
    label: "Configured",
    title: "Read from the configuration the controller holds. Says what was asked for — not proof it is running.",
    mark: "border border-ink-muted",
    chip: "border-line text-ink-muted",
  },
  reported: {
    label: "Reported",
    title: "Sent by the gateway itself, in a dated state record.",
    mark: "rounded-full bg-ink",
    chip: "border-line bg-surface-2 text-ink",
  },
  unverified: {
    label: "Unverified",
    title: "The firmware can report this, but it has never been observed populated on this hardware.",
    mark: "rounded-full border border-dashed border-ink-subtle",
    chip: "border-dashed border-ink-subtle/60 text-ink-subtle",
  },
};

/** Neutral provenance chip. Never the status ramp: where a value came from is not its health. */
function ProvenanceTag({ kind }: { kind: Provenance }) {
  const p = PROVENANCE[kind];
  return (
    <span
      title={p.title}
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide",
        p.chip,
      )}
    >
      <span aria-hidden className={cn("inline-block h-1.5 w-1.5", p.mark)} />
      {p.label}
    </span>
  );
}

const epochIso = (s: number | null | undefined) =>
  s == null ? null : new Date(s * 1000).toISOString();

/** The page header's evidence line: the generation we hold vs the one the device reported. */
function ProvenanceBar({ data }: { data: GatewayDhcpData }) {
  const s = data.state;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-muted">
      <span className="flex items-center gap-1.5">
        <ProvenanceTag kind="configured" />
        Config #{data.config_uuid ?? "—"}
      </span>
      {s.available ? (
        <span className="flex items-center gap-1.5" title={fmtAbsolute(epochIso(s.recorded))}>
          <ProvenanceTag kind="reported" />
          Device reported config #{s.uuid ?? "?"}
          {s.recorded != null ? ` · ${relTime(epochIso(s.recorded))}` : ""}
        </span>
      ) : (
        <span>No dated state record from this gateway — nothing below is device-reported.</span>
      )}
      {s.matches_config === false && (
        <span className="text-warn">
          The gateway last reported a different config generation, so what is configured here may
          not be what it is running.
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One LAN network
 * ------------------------------------------------------------------ */

/** "192.168.1.10" -> 3232235786, or null. Client-side preview only; the server validates. */
function ipToInt(ip: string): number | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip.trim());
  if (!m) return null;
  const o = [m[1], m[2], m[3], m[4]].map(Number);
  if (o.some((x) => !(x >= 0 && x <= 255))) return null;
  return ((o[0] ?? 0) * 2 ** 24) + ((o[1] ?? 0) << 16) + ((o[2] ?? 0) << 8) + (o[3] ?? 0);
}

function intToIp(n: number): string {
  return [Math.floor(n / 2 ** 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join(".");
}

/** Network base + size from "192.168.1.0/24". */
function parseNet(subnet: string | null): { base: number; size: number } | null {
  if (!subnet) return null;
  const [addr, prefix] = subnet.split("/");
  const base = addr ? ipToInt(addr) : null;
  const len = Number(prefix);
  if (base == null || !(len >= 0 && len <= 32)) return null;
  return { base, size: 2 ** (32 - len) };
}

/** Mirrors the server's LEASE_TIME_RE for an inline hint. The server remains the authority. */
const LEASE_OK = /^[1-9][0-9]{0,4}[smhd]$/;

function ScopeBlock({
  scope,
  support,
  editable,
  onReview,
  onAddFixed,
  onEditFixed,
  onDeleteFixed,
}: {
  scope: GatewayDhcpScope;
  support: GatewayReservationSupport;
  editable: boolean;
  onReview: (change: PendingDhcpChange) => void;
  onAddFixed: () => void;
  onEditFixed: (row: GatewayDhcpReservation) => void;
  onDeleteFixed: (row: GatewayDhcpReservation) => void;
}) {
  const net = parseNet(scope.subnet);
  const hosts = scope.host_count ?? 0;
  const gwOffset =
    net && scope.interface_ip ? (ipToInt(scope.interface_ip) ?? net.base) - net.base : null;

  // Form state seeded from what is CONFIGURED. Addresses in, offsets out.
  const initial = useMemo(
    () => ({
      enabled: scope.enabled,
      first:
        scope.pool?.first_address ??
        (net ? intToIp(net.base + (scope.dhcp?.lease_first ?? 10)) : ""),
      count: String(scope.dhcp?.lease_count ?? 100),
      lease: scope.dhcp?.lease_time ?? "6h",
      dns: (scope.dhcp?.use_dns ?? []).join(", "),
      customDns: (scope.dhcp?.use_dns ?? []).length > 0,
    }),
    [scope, net],
  );
  const [enabled, setEnabled] = useState(initial.enabled);
  const [first, setFirst] = useState(initial.first);
  const [count, setCount] = useState(initial.count);
  const [lease, setLease] = useState(initial.lease);
  const [customDns, setCustomDns] = useState(initial.customDns);
  const [dns, setDns] = useState(initial.dns);

  const firstInt = ipToInt(first);
  const firstOffset = net && firstInt != null ? firstInt - net.base : null;
  const countNum = Number(count);
  const lastOffset = firstOffset != null && countNum >= 1 ? firstOffset + countNum - 1 : null;
  const dnsList = customDns ? dns.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean) : [];

  // Inline previews of the server's refusals, so the operator is not surprised in review.
  const problem = !enabled
    ? null
    : firstOffset == null
      ? "Enter the first address of the range."
      : firstOffset < 1 || firstOffset > hosts
        ? `The first address must be inside ${scope.subnet}.`
        : !(countNum >= 1)
          ? "The range must hand out at least one address."
          : lastOffset != null && lastOffset > hosts
            ? `The range runs past the last usable address (${net ? intToIp(net.base + hosts) : "—"}).`
            : gwOffset != null && lastOffset != null && firstOffset <= gwOffset && gwOffset <= lastOffset
              ? `The range includes the gateway's own address ${scope.interface_ip}.`
              : !LEASE_OK.test(lease)
                ? "Lease time must be a whole number of seconds, minutes, hours or days."
                : dnsList.some((d) => ipToInt(d) == null)
                  ? "DNS servers must be IPv4 addresses, not hostnames."
                  : customDns && dnsList.length === 0
                    ? "Enter at least one DNS server, or use the gateway."
                    : null;

  const dirty =
    enabled !== initial.enabled ||
    (enabled &&
      (first !== initial.first ||
        count !== initial.count ||
        lease !== initial.lease ||
        customDns !== initial.customDns ||
        (customDns && dns !== initial.dns)));

  const reset = () => {
    setEnabled(initial.enabled);
    setFirst(initial.first);
    setCount(initial.count);
    setLease(initial.lease);
    setCustomDns(initial.customDns);
    setDns(initial.dns);
  };

  const review = () =>
    onReview({
      intent: "dhcp.scope",
      label: enabled ? "Save DHCP settings" : "Turn DHCP off",
      spec: {
        interface: scope.name ?? "",
        dhcp_enabled: enabled,
        ...(enabled
          ? {
              dhcp_lease_first: firstOffset ?? undefined,
              dhcp_lease_count: countNum,
              dhcp_lease_time: lease,
              use_dns: dnsList,
            }
          : {}),
      },
    });

  return (
    <section className="md-card overflow-hidden" aria-label={`DHCP on ${scope.name ?? "network"}`}>
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <h2 className="text-base font-semibold text-ink">{scope.name ?? "—"}</h2>
        <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem] text-ink-muted">
          {scope.vlan != null ? `VLAN ${scope.vlan}` : "untagged"}
        </span>
        <span className="font-mono text-xs text-ink-muted">
          {scope.subnet ?? "no subnet"}
          {scope.interface_ip ? ` · gateway ${scope.interface_ip}` : ""}
        </span>
        {scope.ports.map((p) => (
          <span key={p} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem]">
            {p}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-2 text-xs text-ink-subtle">
          {scope.reported_addresses && scope.reported_addresses.length > 0 ? (
            <>
              <ProvenanceTag kind="reported" />
              <span className="font-mono">{scope.reported_addresses.join(", ")}</span>
            </>
          ) : (
            <ProvenanceTag kind="configured" />
          )}
        </span>
      </header>

      <div className="px-4">
        {!scope.dhcp_capable ? (
          <p className="py-4 text-sm text-ink-muted">{scope.not_capable_reason}</p>
        ) : (
          <>
            <SettingRow label="Client addressing">
              <div role="radiogroup" aria-label="Client addressing" className="flex flex-col gap-2 text-sm">
                <Radio
                  checked={!enabled}
                  disabled={!editable}
                  onChange={() => setEnabled(false)}
                  label="Do not respond to DHCP requests"
                />
                <Radio
                  checked={enabled}
                  disabled={!editable}
                  onChange={() => setEnabled(true)}
                  label="Run a DHCP server on this network"
                />
              </div>
            </SettingRow>

            {enabled && (
              <>
                <SettingRow
                  label="DHCP range"
                  hint="Stored on the gateway as an offset from the start of the subnet."
                >
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-ink-muted">First address</span>
                      <input
                        value={first}
                        disabled={!editable}
                        onChange={(e) => setFirst(e.target.value)}
                        className={`${INPUT} min-h-touch w-44 font-mono`}
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-ink-muted">Number of addresses</span>
                      <input
                        inputMode="numeric"
                        value={count}
                        disabled={!editable}
                        onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ""))}
                        className={`${INPUT} min-h-touch w-28 font-mono`}
                      />
                    </label>
                    <div className="pb-2 font-mono text-xs text-ink-muted">
                      {net && firstOffset != null && lastOffset != null
                        ? `→ ${intToIp(net.base + firstOffset)} – ${intToIp(net.base + lastOffset)} · offset ${firstOffset}`
                        : "—"}
                    </div>
                  </div>
                  {net && (
                    <AddressStrip
                      hosts={hosts}
                      base={net.base}
                      gwOffset={gwOffset}
                      firstOffset={firstOffset}
                      lastOffset={lastOffset}
                      fixed={scope.reservations}
                    />
                  )}
                </SettingRow>

                <SettingRow label="Lease time">
                  <LeaseTimeInput value={lease} onChange={setLease} disabled={!editable} />
                </SettingRow>

                <SettingRow label="DNS servers">
                  <div role="radiogroup" aria-label="DNS servers" className="flex flex-col gap-2 text-sm">
                    <Radio
                      checked={!customDns}
                      disabled={!editable}
                      onChange={() => setCustomDns(false)}
                      label="Use the gateway as DNS"
                    />
                    <Radio
                      checked={customDns}
                      disabled={!editable}
                      onChange={() => setCustomDns(true)}
                      label="Specify DNS servers"
                    />
                    {customDns && (
                      <input
                        value={dns}
                        disabled={!editable}
                        onChange={(e) => setDns(e.target.value)}
                        placeholder="10.0.0.53, 10.0.0.54"
                        aria-label="DNS server addresses"
                        className={`${INPUT} min-h-touch w-full max-w-sm font-mono`}
                      />
                    )}
                  </div>
                </SettingRow>
              </>
            )}

            <SettingRow label="Reserved IP ranges">
              <p className="text-sm text-ink-muted">
                Not available on this gateway — the configuration holds one contiguous range per
                network, with no excluded ranges.
              </p>
            </SettingRow>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line/60 py-3">
              {problem && dirty && <span className="mr-auto text-xs text-critical">{problem}</span>}
              <Button variant="ghost" disabled={!dirty} onClick={reset}>
                Reset
              </Button>
              <RoleGate min="org_admin" mode="hide">
                <Button
                  variant="primary"
                  disabled={!editable || !dirty || !!problem}
                  onClick={review}
                >
                  Review changes
                </Button>
              </RoleGate>
            </div>
          </>
        )}

        <FixedIpSection
          scope={scope}
          support={support}
          editable={editable}
          onAdd={onAddFixed}
          onEdit={onEditFixed}
          onDelete={onDeleteFixed}
        />

        <LeasesPanel leases={scope.leases} />
      </div>
    </section>
  );
}

/** Meraki MX setting row: the name on the left, the control on the right; stacks on narrow. */
function SettingRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid gap-2 border-t border-line/60 py-3 first:border-t-0 md:grid-cols-[12rem_1fr] md:gap-6">
      <div>
        <div className="text-sm font-medium text-ink">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-ink-subtle">{hint}</div>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{children}</h3>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[0.8125rem]">{children}</span>;
}

function Radio({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className={cn("flex min-h-[1.75rem] items-center gap-2", disabled && "opacity-60")}>
      <input
        type="radio"
        className="accent-brand"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="text-ink">{label}</span>
    </label>
  );
}

const UNITS: { value: string; label: string }[] = [
  { value: "m", label: "minutes" },
  { value: "h", label: "hours" },
  { value: "d", label: "days" },
  { value: "s", label: "seconds" },
];

/** Number + unit. A free-text box invites "3600" and "infinite", both of which the gateway's
 *  schema rejects; a picker cannot produce either. */
function LeaseTimeInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const m = /^(\d*)([smhdw])$/.exec(value);
  const amount = m?.[1] ?? value.replace(/[^0-9]/g, "");
  const unit = m?.[2] ?? "h";
  // A device-side "1w" is valid in the schema but not accepted by this write path; show it
  // honestly so the operator can see why a save is refused, rather than silently re-unit it.
  const units = unit === "w" ? [...UNITS, { value: "w", label: "weeks (not accepted)" }] : UNITS;
  return (
    <div className="flex flex-wrap gap-2">
      <input
        inputMode="numeric"
        value={amount}
        disabled={disabled}
        aria-label="Lease time amount"
        onChange={(e) => onChange(`${e.target.value.replace(/[^0-9]/g, "")}${unit}`)}
        className={`${INPUT} min-h-touch w-24 font-mono`}
      />
      <select
        value={unit}
        disabled={disabled}
        aria-label="Lease time unit"
        onChange={(e) => onChange(`${amount}${e.target.value}`)}
        className={`${INPUT} min-h-touch`}
      >
        {units.map((u) => (
          <option key={u.value} value={u.value}>
            {u.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The subnet's usable hosts drawn to scale: the gateway's own address, the dynamic range and
 * each fixed IP. What an off-by-one in an offset looks like before it lands on a guest network.
 * Neutral ink throughout — this is a map of configuration, not an operational state.
 */
function AddressStrip({
  hosts,
  base,
  gwOffset,
  firstOffset,
  lastOffset,
  fixed,
}: {
  hosts: number;
  base: number;
  gwOffset: number | null;
  firstOffset: number | null;
  lastOffset: number | null;
  fixed: GatewayDhcpReservation[];
}) {
  if (hosts < 2) return null;
  const pct = (off: number) => `${(Math.min(Math.max(off, 1), hosts) - 1) / (hosts - 1) * 100}%`;
  const poolOk = firstOffset != null && lastOffset != null && firstOffset >= 1 && lastOffset >= firstOffset;
  const poolLabel = poolOk ? `${lastOffset! - firstOffset! + 1} of ${hosts} usable addresses` : "no valid range";
  return (
    <div className="mt-3 max-w-2xl">
      <div
        role="img"
        aria-label={`Address map: gateway ${gwOffset != null ? intToIp(base + gwOffset) : "unknown"}, dynamic range ${poolLabel}, ${fixed.length} fixed IP assignment(s)`}
        className="relative h-6 overflow-hidden rounded border border-line bg-surface-2"
      >
        {poolOk && (
          <div
            className="absolute inset-y-0 border-x border-ink/50 bg-ink/15"
            style={{
              left: pct(firstOffset!),
              width: `calc(${pct(Math.min(lastOffset!, hosts))} - ${pct(firstOffset!)} + 2px)`,
            }}
          />
        )}
        {fixed.map((r) =>
          r.static_lease_offset != null ? (
            <div
              key={r.macaddr}
              title={`${r.resolved_address ?? ""} ${r.label ?? r.macaddr}`}
              className="absolute inset-y-1 w-0.5 bg-ink-muted"
              style={{ left: pct(r.static_lease_offset) }}
            />
          ) : null,
        )}
        {gwOffset != null && (
          <div className="absolute inset-y-0 w-1 bg-ink" style={{ left: pct(gwOffset) }} />
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.6875rem] text-ink-subtle">
        <span className="flex items-center gap-1">
          <span aria-hidden className="inline-block h-2.5 w-1 bg-ink" /> gateway
          {gwOffset != null ? ` ${intToIp(base + gwOffset)}` : ""}
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden className="inline-block h-2.5 w-3 border-x border-ink/50 bg-ink/15" />
          dynamic range · {poolLabel}
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden className="inline-block h-2 w-0.5 bg-ink-muted" /> fixed IP
        </span>
        <span className="ml-auto font-mono">
          {intToIp(base + 1)} … {intToIp(base + hosts)}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Fixed IP assignments (ipv4.dhcp-leases[])
 * ------------------------------------------------------------------ */

function FixedIpNotice({ support }: { support: GatewayReservationSupport }) {
  return (
    <div role="note" className="md-card mt-2 border-warn/40 bg-warn-soft p-3 text-sm">
      <div className="font-medium text-ink">
        {support.functional === false
          ? "Saved to the gateway — not applied by its firmware"
          : "Not yet confirmed to work on this firmware"}
      </div>
      <p className="mt-1 text-ink-muted">{support.reason}</p>
      <p className="mt-1 text-xs text-ink-subtle">
        Reported firmware: <Mono>{support.firmware ?? "unknown"}</Mono>
      </p>
    </div>
  );
}

function FixedIpSection({
  scope,
  support,
  editable,
  onAdd,
  onEdit,
  onDelete,
}: {
  scope: GatewayDhcpScope;
  support: GatewayReservationSupport;
  editable: boolean;
  onAdd: () => void;
  onEdit: (row: GatewayDhcpReservation) => void;
  onDelete: (row: GatewayDhcpReservation) => void;
}) {
  const rows = scope.reservations;
  // An incapable interface with no assignments has nothing to say here.
  if (!scope.dhcp_capable && rows.length === 0) return null;
  return (
    <div className="border-t border-line/60 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>Fixed IP assignments</SectionTitle>
        <RoleGate min="org_admin" mode="hide">
          <Button
            variant="secondary"
            disabled={!editable || !scope.enabled || !scope.dhcp_capable}
            title={!scope.enabled ? "Turn the DHCP server on first" : undefined}
            onClick={onAdd}
          >
            Add fixed IP
          </Button>
        </RoleGate>
      </div>

      <FixedIpNotice support={support} />

      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">None configured.</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="border-b border-line [&>th]:px-2 [&>th]:py-2 [&>th]:font-medium">
                <th>
                  Name
                  <span className="block text-[0.625rem] font-normal">Meridian only</span>
                </th>
                <th>MAC address</th>
                <th>IP address</th>
                <th>Lease</th>
                <th>Publish hostname</th>
                <th>On the gateway</th>
                <th className="w-40" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.macaddr}
                  className="border-t border-line/60 [&>td]:px-2 [&>td]:py-2 hover:bg-surface-2/60"
                >
                  <td className="text-ink">{r.label ?? <span className="text-ink-subtle">—</span>}</td>
                  <td className="font-mono text-ink-muted">{r.macaddr}</td>
                  <td className="font-mono text-ink">
                    {r.resolved_address ?? `offset ${r.static_lease_offset ?? "?"}`}
                    {r.in_pool && (
                      <div className="text-[0.6875rem] font-sans text-ink-subtle">
                        inside the dynamic range
                      </div>
                    )}
                  </td>
                  <td className="font-mono text-ink-muted">{r.lease_time}</td>
                  <td className="text-ink-muted">{r.publish_hostname ? "yes" : "no"}</td>
                  <td>
                    <span
                      className={cn(
                        "whitespace-nowrap rounded border px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide",
                        r.functional === false
                          ? "border-warn/50 text-warn"
                          : "border-dashed border-ink-subtle/60 text-ink-subtle",
                      )}
                    >
                      {r.functional === false ? "Not applied" : "Unverified"}
                    </span>
                  </td>
                  <td className="text-right">
                    <RoleGate min="org_admin" mode="hide">
                      <div className="flex justify-end gap-1">
                        <Button variant="secondary" disabled={!editable} onClick={() => onEdit(r)}>
                          Edit
                        </Button>
                        <Button variant="ghost" disabled={!editable} onClick={() => onDelete(r)}>
                          Delete
                        </Button>
                      </div>
                    </RoleGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Current leases — device evidence, never the configured range
 * ------------------------------------------------------------------ */

const LEASE_HEADLINE: Record<GatewayLeaseVisibility["state"], string> = {
  observed: "Reported by the gateway",
  not_observed: "Not reported",
  interface_down: "Interface down",
  no_state: "No device state",
};

function LeasesPanel({ leases }: { leases: GatewayLeaseVisibility }) {
  const recorded = epochIso(leases.recorded);
  return (
    <div className="border-t border-line/60 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <SectionTitle>Current leases</SectionTitle>
        {leases.state === "observed" ? (
          <ProvenanceTag kind="reported" />
        ) : leases.state === "not_observed" ? (
          <ProvenanceTag kind="unverified" />
        ) : null}
        <span className="ml-auto text-xs text-ink-muted" title={fmtAbsolute(recorded)}>
          {LEASE_HEADLINE[leases.state]}
          {recorded ? ` · state record ${relTime(recorded)}` : ""}
        </span>
      </div>

      {leases.state === "observed" ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="border-b border-line [&>th]:px-2 [&>th]:py-2 [&>th]:font-medium">
                <th>MAC address</th>
                <th>Address</th>
                <th>Hostname</th>
                <th>Lease stamp</th>
              </tr>
            </thead>
            <tbody>
              {leases.items.map((l, i) => (
                <tr key={`${l.mac}-${i}`} className="border-t border-line/60 [&>td]:px-2 [&>td]:py-2">
                  <td className="font-mono text-ink-muted">{l.mac ?? "—"}</td>
                  <td className="font-mono text-ink">{l.address ?? "—"}</td>
                  <td className="text-ink-muted">{l.hostname ?? "—"}</td>
                  <td className="text-ink-muted">
                    {typeof l.assigned === "number" ? fmtAbsolute(epochIso(l.assigned)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-ink-subtle">
            Best effort: the gateway lists a lease only when the client is also in its bridge table
            with a learned address, so a client can hold an address without appearing here.
          </p>
        </div>
      ) : (
        <div className="mt-2 rounded border border-dashed border-line p-3 text-sm">
          <p className="text-ink-muted">{leases.detail}</p>
          <p className="mt-1 text-xs text-ink-subtle">
            This panel shows only what the gateway reports. The DHCP range above is what it is
            configured to hand out — not who holds an address.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Add / edit a fixed IP. Produces a SPEC — it never applies anything itself.
 * ------------------------------------------------------------------ */

/** Client-side preview of the server's MAC normalizer (gateway_config.normalize_mac). */
function previewMac(value: string): string | null {
  const t = value.trim().toLowerCase();
  const ok =
    /^[0-9a-f]{2}([:-])[0-9a-f]{2}(?:\1[0-9a-f]{2}){4}$/.test(t) ||
    /^[0-9a-f]{4}\.[0-9a-f]{4}\.[0-9a-f]{4}$/.test(t) ||
    /^[0-9a-f]{12}$/.test(t);
  if (!ok) return null;
  const d = t.replace(/[^0-9a-f]/g, "");
  return (d.match(/.{2}/g) ?? []).join(":");
}

function FixedIpDialog({
  scope,
  row,
  support,
  onClose,
  onReview,
  onLabelOnly,
}: {
  scope: GatewayDhcpScope;
  row?: GatewayDhcpReservation;
  support: GatewayReservationSupport;
  onClose: () => void;
  onReview: (change: PendingDhcpChange) => void;
  onLabelOnly: (label: string) => void;
}) {
  const isEdit = !!row;
  const net = parseNet(scope.subnet);
  const [mac, setMac] = useState(row?.macaddr ?? "");
  const [address, setAddress] = useState(row?.resolved_address ?? "");
  const [label, setLabel] = useState(row?.label ?? "");
  const [lease, setLease] = useState(row?.lease_time ?? "6h");
  const [publish, setPublish] = useState(row?.publish_hostname ?? true);

  const normalized = previewMac(mac);
  const addrInt = ipToInt(address);
  const offset = net && addrInt != null ? addrInt - net.base : null;
  const inSubnet = offset != null && net != null && offset >= 1 && offset <= net.size - 2;
  const inPool =
    inSubnet && scope.dhcp?.lease_first != null && scope.dhcp.lease_count != null
      ? offset >= scope.dhcp.lease_first && offset <= scope.dhcp.lease_first + scope.dhcp.lease_count - 1
      : false;

  const deviceUnchanged =
    isEdit &&
    normalized === row!.macaddr &&
    address.trim() === (row!.resolved_address ?? "") &&
    lease === row!.lease_time &&
    publish === row!.publish_hostname;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!normalized) return;
    const iface = scope.name ?? "";
    if (deviceUnchanged) {
      // Only the controller-side name changed: nothing to push, so no review step.
      if (label.trim() !== (row!.label ?? "")) onLabelOnly(label.trim());
      else onClose();
      return;
    }
    const macChanged = isEdit && normalized !== row!.macaddr;
    onReview({
      intent: "dhcp.reservation.upsert",
      label: isEdit ? "Save fixed IP" : "Add fixed IP",
      spec: {
        interface: iface,
        macaddr: mac.trim(),
        address: address.trim(),
        lease_time: lease,
        publish_hostname: publish,
        ...(isEdit ? { original_macaddr: row!.macaddr } : {}),
      },
      labelFollowUp:
        label.trim() || (isEdit && row!.label)
          ? {
              interface: iface,
              set: { macaddr: normalized, label: label.trim() },
              clear: macChanged && row!.label ? [row!.macaddr] : undefined,
            }
          : undefined,
    });
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {isEdit ? `Edit fixed IP on ${scope.name}` : `Add fixed IP on ${scope.name}`}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Nothing is sent to the gateway until you review the change on the next step.
          </Dialog.Description>

          <FixedIpNotice support={support} />

          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">MAC address</span>
                <input
                  value={mac}
                  onChange={(e) => setMac(e.target.value)}
                  placeholder="aa:bb:cc:dd:ee:ff"
                  className={`${INPUT} min-h-touch font-mono`}
                />
                <span className={cn("text-xs", mac && !normalized ? "text-critical" : "text-ink-subtle")}>
                  {!mac
                    ? "Colon, dash or Cisco dot form."
                    : normalized
                      ? `Saved as ${normalized}`
                      : "Not a MAC address."}
                </span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">IP address</span>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={net ? intToIp(net.base + 200) : ""}
                  className={`${INPUT} min-h-touch font-mono`}
                />
                <span
                  className={cn(
                    "text-xs",
                    address && !inSubnet ? "text-critical" : inPool ? "text-warn" : "text-ink-subtle",
                  )}
                >
                  {!address
                    ? `Inside ${scope.subnet ?? "the subnet"}.`
                    : !inSubnet
                      ? `Must be a usable address inside ${scope.subnet}.`
                      : inPool
                        ? `Inside the dynamic range — another client can be handed it first. Stored as offset ${offset}.`
                        : `Stored as offset ${offset}: it moves if the subnet changes.`}
                </span>
              </label>
            </div>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input
                value={label}
                maxLength={128}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Front-desk printer"
                className={`${INPUT} min-h-touch`}
              />
              <span className="text-xs text-ink-subtle">
                Kept in Meridian only. The gateway's configuration has no name field, so this never
                reaches the device.
              </span>
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Lease time</span>
                <LeaseTimeInput value={lease} onChange={setLease} disabled={false} />
              </div>
              <label className="flex items-start gap-2 pt-6 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-brand"
                  checked={publish}
                  onChange={(e) => setPublish(e.target.checked)}
                />
                <span>
                  <span className="text-ink">Publish hostname</span>
                  <span className="block text-xs text-ink-subtle">
                    Stored in the config; this firmware's templates do not read it.
                  </span>
                </span>
              </label>
            </div>

            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!normalized || !inSubnet || !LEASE_OK.test(lease)}
              >
                {deviceUnchanged ? "Save name" : "Review change"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------ *
 * Review delta — per network, per fixed IP
 * ------------------------------------------------------------------ */

function DhcpDelta({ plan }: { plan: GatewayPlan }) {
  const before = plan.dhcp_before?.scopes ?? [];
  const after = plan.dhcp_after?.scopes ?? [];
  const key = (s: GatewayDhcpScope) => s.name ?? s.location;
  const beforeBy = new Map(before.map((s) => [key(s), s]));
  const afterBy = new Map(after.map((s) => [key(s), s]));
  const names = Array.from(new Set([...beforeBy.keys(), ...afterBy.keys()]));

  const scopeLine = (s?: GatewayDhcpScope) =>
    !s
      ? "—"
      : !s.dhcp
        ? "DHCP off"
        : `${s.pool ? `${s.pool.first_address} – ${s.pool.last_address} (${s.pool.size})` : `offset ${s.dhcp.lease_first} × ${s.dhcp.lease_count}`} · ${s.dhcp.lease_time ?? "—"} · DNS ${s.dhcp.use_dns.length ? s.dhcp.use_dns.join(", ") : "gateway"}`;
  const fixedLine = (r: GatewayDhcpReservation) =>
    `${r.resolved_address ?? `offset ${r.static_lease_offset}`} · ${r.lease_time}${r.publish_hostname ? "" : " · hostname not published"}`;

  const items: ReactNode[] = [];
  for (const n of names) {
    const b = beforeBy.get(n);
    const a = afterBy.get(n);
    const scopeKey = (s?: GatewayDhcpScope) => JSON.stringify(s ? [s.subnet, s.dhcp] : null);
    if (scopeKey(b) !== scopeKey(a)) {
      items.push(
        <li key={`scope-${n}`} className="px-4 py-2 text-xs">
          <div className="font-medium text-ink">
            {n} DHCP <DeltaChip kind={!b ? "added" : !a ? "removed" : "changed"} />
          </div>
          {b && <div className="font-mono text-ink-subtle">before: {scopeLine(b)}</div>}
          {a && <div className="font-mono text-ink-muted">after: {scopeLine(a)}</div>}
        </li>,
      );
    }
    const rb = new Map((b?.reservations ?? []).map((r) => [r.macaddr, r]));
    const ra = new Map((a?.reservations ?? []).map((r) => [r.macaddr, r]));
    for (const mac of new Set([...rb.keys(), ...ra.keys()])) {
      const x = rb.get(mac);
      const y = ra.get(mac);
      if (x && y && fixedLine(x) === fixedLine(y)) continue;
      items.push(
        <li key={`fixed-${n}-${mac}`} className="px-4 py-2 text-xs">
          <div className="font-medium text-ink">
            {n} · fixed IP <span className="font-mono">{mac}</span>{" "}
            <DeltaChip kind={!x ? "added" : !y ? "removed" : "changed"} />
          </div>
          {x && <div className="font-mono text-ink-subtle">before: {fixedLine(x)}</div>}
          {y && <div className="font-mono text-ink-muted">after: {fixedLine(y)}</div>}
        </li>,
      );
    }
  }

  return (
    <div className="md-card overflow-hidden">
      <div className="border-b border-line px-4 py-2 text-sm font-medium text-ink">
        {items.length} DHCP change{items.length === 1 ? "" : "s"}
      </div>
      <ul className="divide-y divide-line">
        {items.length ? (
          items
        ) : (
          <li className="px-4 py-3 text-xs text-ink-muted">
            No DHCP setting changes — see the configuration diff below.
          </li>
        )}
      </ul>
    </div>
  );
}
